/**
 * Pure helpers for endless free-play escalation after the scripted open.
 */
import type {
  EscalationConfig,
  EscalationSpawnChoice,
  EscalationStrategy,
  SpawnEvent,
} from "../data/scenario";
import type { LayerId } from "./board/types";

export type EscalationRuntime = {
  /** Elapsed time when strategies became active (script end + lead-in) */
  startAt: number;
  strategyIndex: number;
  /** Elapsed when current strategy started */
  strategyStartedAt: number;
  /** Elapsed of last escalation spawn attempt */
  lastSpawnAt: number;
  /** How many full strategy rotations completed */
  cycles: number;
  active: boolean;
};

export function scriptEndTime(spawns: readonly SpawnEvent[]): number {
  if (!spawns.length) return 0;
  let max = 0;
  for (const s of spawns) if (s.at > max) max = s.at;
  return max;
}

export function createEscalationRuntime(): EscalationRuntime {
  return {
    startAt: 0,
    strategyIndex: 0,
    strategyStartedAt: 0,
    lastSpawnAt: 0,
    cycles: 0,
    active: false,
  };
}

export function pickWeighted(
  pool: readonly EscalationSpawnChoice[],
  rand: () => number = Math.random,
): EscalationSpawnChoice | null {
  if (!pool.length) return null;
  let total = 0;
  for (const c of pool) total += Math.max(0, c.weight);
  if (total <= 0) return pool[0] ?? null;
  let r = rand() * total;
  for (const c of pool) {
    r -= Math.max(0, c.weight);
    if (r <= 0) return c;
  }
  return pool[pool.length - 1] ?? null;
}

/**
 * Interval shrinks each cycle and slightly within long runs.
 * Floor keeps the board from becoming pure spawn spam.
 */
export function effectiveInterval(
  strategy: EscalationStrategy,
  cycles: number,
  difficultyMul: number,
): number {
  const cycleMul = Math.pow(0.9, cycles);
  const raw = strategy.baseInterval * cycleMul * difficultyMul;
  return Math.max(2.8, raw);
}

export function currentStrategy(
  config: EscalationConfig,
  runtime: EscalationRuntime,
): EscalationStrategy | null {
  const list = config.strategies;
  if (!list.length) return null;
  return list[runtime.strategyIndex % list.length] ?? null;
}

/**
 * Advance strategy rotation if the current protocol has run long enough.
 * Returns true when the strategy (and wave label) changed.
 */
export function maybeAdvanceStrategy(
  config: EscalationConfig,
  runtime: EscalationRuntime,
  elapsed: number,
): boolean {
  const list = config.strategies;
  if (!list.length || !runtime.active) return false;
  const strat = list[runtime.strategyIndex % list.length]!;
  // Strategies get slightly shorter each full cycle
  const phaseLen = Math.max(16, strat.duration * Math.pow(0.94, runtime.cycles));
  if (elapsed - runtime.strategyStartedAt < phaseLen) return false;

  runtime.strategyIndex += 1;
  if (runtime.strategyIndex % list.length === 0) {
    runtime.cycles += 1;
  }
  runtime.strategyStartedAt = elapsed;
  return true;
}

export function layerOf(choice: EscalationSpawnChoice): LayerId {
  return choice.layer ?? "mind";
}
